import type { ChangeEvent } from "react";
import { bound, observable } from "ecce-react";


export type SignUpRequest = {
	plan: string | null;
	quantity: number | null;

	email: string;

	firstName: string;
	lastName: string;
	phone: string;

	companyName: string;
	address: string;
	city: string;
	state: string;
	postcode: string;
	country: string;
	website: string;
};
namespace SignUpRequest {
	export type Key = keyof SignUpRequest;
}

export class Attribute<T extends string | number | null> {
	@observable() accessor value: T;

	constructor(init: T) {
		this.value = init;
	}

	@bound()
	handleChange(ev: ChangeEvent<{ value: T }>): void {
		this.value = ev.target.value;
	}
}

type AttributeState = { [Key in SignUpRequest.Key]: Attribute<SignUpRequest[Key]> };

export class SignUpRequestState {
	readonly #state: AttributeState = {
		plan: new Attribute(""),
		quantity: new Attribute(null),
		email: new Attribute(""),
		firstName: new Attribute(""),
		lastName: new Attribute(""),
		phone: new Attribute(""),
		companyName: new Attribute(""),
		address: new Attribute(""),
		city: new Attribute(""),
		state: new Attribute(""),
		postcode: new Attribute(""),
		country: new Attribute(""),
		website: new Attribute(""),
	};

	#latitude: number | undefined = undefined;
	#longitude: number | undefined = undefined;

	attribute<Key extends SignUpRequest.Key>(key: Key): Attribute<SignUpRequest[Key]> {
		return this.#state[key];
	}

	setLatLng(latitude: number, longitude: number): void {
		this.#latitude = latitude;
		this.#longitude = longitude;
	}

	@bound()
	resetLatLng(): void {
		this.#latitude = undefined;
		this.#longitude = undefined;
	}

	make(): SignUpRequest {
		const request = {
			...Object.fromEntries(
				Object.entries(this.#state).map(([ key, attr ]) => [ key, attr.value ])
			) as SignUpRequest,
			latitude: this.#latitude,
			longitude: this.#longitude,
		};
		return request;
	}
}
